import * as THREE from 'three'

import { RUG_THICKNESS } from 'Renderer/constants'

class FootprintMaterial extends THREE.ShaderMaterial {

    constructor(floorPlane, occlusionTexture, resolution) {
        super()

        this.isFootprintMaterial = true

        this.depthWrite = false
        this.depthTest = false
        this.colorWrite = false
        this.stencilWrite = true
        this.stencilFunc = THREE.AlwaysStencilFunc
        this.stencilRef = 2
        this.stencilFail = THREE.ReplaceStencilOp
        this.stencilZFail = THREE.ReplaceStencilOp
        this.stencilZPass = THREE.ReplaceStencilOp

        this.rugSurfacePlane = new THREE.Plane().copy(floorPlane)
        this.rugSurfacePlane.constant -= RUG_THICKNESS

        this.clipping = true
        this.clipIntersection = true
        this.clippingPlanes = [
            this.rugSurfacePlane
        ]

        this.uniforms = {
            invModelMatrix: { value: new THREE.Matrix4() },
            occlusionMap: { value: occlusionTexture },
            resolution: { value: resolution },
        }

        this.vertexShader = `
            #include <common>

            #include <morphtarget_pars_vertex>
            #include <skinning_pars_vertex>
            #include <clipping_planes_pars_vertex>
            #if NUM_CLIPPING_PLANES > 0
                uniform vec4 clippingPlanes[NUM_CLIPPING_PLANES];
            #endif

            varying vec4 projTexCoord;
            varying vec4 vPosition;
            uniform mat4 textureMatrix;

            uniform mat4 invModelMatrix;

            void main() {
                vec4 transformed = vec4(position.xyz, 1.0);

                #if NUM_CLIPPING_PLANES > 0
                    vClipPosition = -(modelViewMatrix * transformed).xyz;
                #endif 

                vec4 worldPosition = modelMatrix * transformed;

                #if NUM_CLIPPING_PLANES > 0
                    // project on the rug plane
                    vec4 plane = clippingPlanes[0];
                    worldPosition.xyz = worldPosition.xyz + plane.xyz * -(dot(plane.xyz, worldPosition.xyz) + plane.w);
                #endif

                transformed = invModelMatrix * worldPosition;

                vec4 mvPosition = modelViewMatrix * transformed;
                gl_Position = projectionMatrix * mvPosition;
                vPosition = mvPosition;

                projTexCoord = textureMatrix * worldPosition;
            }
        `

        this.fragmentShader = `
            #include <packing>
            #include <clipping_planes_pars_fragment>

            uniform sampler2D occlusionMap;
            uniform vec2 resolution;

            void main() {
                #if NUM_CLIPPING_PLANES > 0
                    vec4 plane;

                    #pragma unroll_loop_start
                    for ( int i = 0; i < UNION_CLIPPING_PLANES; i ++ ) {

                        plane = clippingPlanes[ i ];
                        if ( dot( vClipPosition, plane.xyz ) < plane.w ) discard;

                    }
                    #pragma unroll_loop_end

                    #if UNION_CLIPPING_PLANES < NUM_CLIPPING_PLANES
                        bool clipped = true;

                        #pragma unroll_loop_start
                        for ( int i = UNION_CLIPPING_PLANES; i < NUM_CLIPPING_PLANES; i ++ ) {
                            plane = clippingPlanes[ i ];
                            clipped = ( dot( vClipPosition, plane.xyz ) < plane.w ) && clipped;
                        }
                        #pragma unroll_loop_end

                        if ( clipped ) discard;
                    #endif
                #endif

                // Salma Jiddi: depth_occlusion_0: The ALPHA channel's most significant bit 
                // contains the inverse floor mask where:
                // False: Areas that were segmented as floor.
                // True: All other regions.
                float occlusionAlpha = texture2D(occlusionMap, (gl_FragCoord.xy) / resolution).a;
                if (occlusionAlpha > 0.0) {
                    //gl_FragColor = vec4(1.0, 0.0, 0.0, 1.0);
                    discard;
                } else {
                    gl_FragColor = vec4(0.0, 0.5, 0.5, 1.0);
                }
            }
        `
    }

    /* eslint-disable no-param-reassign */
    static refreshUniforms(renderer, scene, camera, geometry, material, group, mesh) {
        if (!material.isFootprintMaterial) {
            return
        }
        material.uniforms.invModelMatrix.value.copy(mesh.matrixWorld)
        material.uniforms.invModelMatrix.value.invert()
        material.uniformsNeedUpdate = true
    }
    /* eslint-enable no-param-reassign */
}

export default FootprintMaterial